import type { MessageReferences } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { SendReferences } from './send-references';

// @-mention an agent (v1: ONE mention per turn). The composer inserts the literal
// `@AgentName ` token into the text; the STRUCTURED agent id rides the send as
// `references.mentionedAgentId` — a routing signal the harness lifts onto the
// persisted USER row's metadata. Mirrors the native clients' trigger rules
// (@ at a word boundary, whitespace dismisses, case-insensitive name filter).

export interface MentionAgent {
  id: string;
  name: string;
}

export type PendingMention = MentionAgent;

export interface MentionTrigger {
  start: number;
  /** What was typed after the `@` — the panel's filter string. */
  query: string;
}

export const MENTION_ID_MAX_LENGTH = 64;

/** Non-empty, trimmed, ≤64 chars — anything else is dropped silently (never blocks the send). */
export function sanitizeMentionedAgentId(id: string | null | undefined): string | undefined {
  const trimmed = id?.trim();
  return trimmed && trimmed.length <= MENTION_ID_MAX_LENGTH ? trimmed : undefined;
}

/**
 * The active mention trigger: a trailing `@word` whose `@` sits at a word boundary
 * (start of text or right after whitespace). Whitespace after the `@` ends the flow —
 * the trigger no longer parses, so the panel closes. A mid-word `@` (an email) never triggers.
 */
export function detectMentionTrigger(text: string): MentionTrigger | null {
  const at = text.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(text[at - 1])) return null;
  const query = text.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, query };
}

export function filterAgentsByMention<T extends { name: string }>(agents: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return agents;
  return agents.filter((agent) => agent.name.toLowerCase().includes(q));
}

export function mentionToken(name: string): string {
  return `@${name.trim()}`;
}

export function applyMentionPick(text: string, triggerStart: number, name: string): string {
  return `${text.slice(0, triggerStart)}${mentionToken(name)} `;
}

/**
 * The id to send for the pending mention — only while its literal `@AgentName` token is still
 * in the outgoing text (the user deleting the token drops the structured id).
 */
export function mentionedAgentIdForSend(
  pending: PendingMention | null | undefined,
  text: string,
): string | undefined {
  if (!pending) return undefined;
  const id = sanitizeMentionedAgentId(pending.id);
  if (!id || !text.includes(mentionToken(pending.name))) return undefined;
  return id;
}

/** `references` with its riders (the wire is a Struct — no proto change). Defined in
 *  `lib/send-references.ts`, which documents every rider in one place; re-exported here because this is
 *  where callers of `withMentionReference` already look. */
export type { SendReferences };

export function withMentionReference(
  references: MessageReferences | undefined,
  pending: PendingMention | null | undefined,
  text: string,
): SendReferences | undefined {
  const mentionedAgentId = mentionedAgentIdForSend(pending, text);
  if (!mentionedAgentId) return references;
  return { ...(references ?? {}), mentionedAgentId };
}
